import React, { ElementType, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  Spinner,
  useToast,
  Tag,
  HStack,
  VStack,
  Divider,
  Icon,
  Button,
  Badge,
  useDisclosure,
  Card,
  CardHeader,
  CardBody,
} from "@chakra-ui/react";
import {
  FaBuilding,
  FaEnvelope,
  FaGlobe,
  FaPhone,
  FaMapMarkerAlt,
  FaIndustry,
  FaUserShield,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaCrown,
  FaRedoAlt,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { getOrganization } from "../hooks/org_data";
import { Organization } from "../utils/types";
import { FiEdit2 } from "react-icons/fi";
import DeleteOrganizationComponent from "./DeleteOrganizationComponent";

const OrganizationManagementPage: React.FC = () => {
  const { orgId } = useParams();
  const toast = useToast();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const fetchOrganization = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getOrganization(orgId || "");
      if ((res as any)?.success) {
        setOrganization((res as any).data as Organization);
      } else {
        setLoadError("Failed to load organization data.");
      }
    } catch (error) {
      console.error(error);
      setLoadError("An unexpected error occurred while loading organization.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganization();
  }, [orgId]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="80vh">
        <Spinner size="xl" color="#006400" />
      </Flex>
    );
  }

  if (loadError || !organization) {
    return (
      <Flex justify="center" align="center" h="80vh" direction="column">
        <Text color="red.500" fontSize="lg" mb={3}>
          {loadError}
        </Text>
        <Button onClick={fetchOrganization} colorScheme="green" leftIcon={<Icon as={FaRedoAlt as ElementType} />}>
          Retry
        </Button>
      </Flex>
    );
  }

  const org = organization;
  const sub = org.subscription;
  

  return (
    <Box p={6} bg="gray.50" justifyContent={"flex-start"}>
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Icon as={FaBuilding as ElementType} boxSize={7} color="#006400" />
          <Heading size="lg" color="gray.800">
            {org.name}
          </Heading>
          <Badge colorScheme={org.is_active ? "green" : "red"} ml={2}>
            {org.is_active ? "Active" : "Inactive"}
          </Badge>
          <Button
            leftIcon={<Icon as={FiEdit2 as ElementType} />}
            colorScheme="green"
            bg={'#006400'}
            onClick={()=>navigate(`/organization/${org.id}/info/edit`)}
            data-testid="edit-org-btn"
          >
            Edit
          </Button>
        </HStack>
      </Flex>

      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        gap={6}
        alignItems="stretch"
      >
        {/* Organization Details */}
        <Card shadow="sm" borderRadius="2xl">
          <CardHeader borderBottomWidth="1px">
            <HStack>
              <Icon as={FaUserShield as ElementType} color="#006400" />
              <Heading size="md">Organization Details</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="start" spacing={3}>
              {org.description && (
                <Text fontSize="md" color="gray.600">
                  {org.description}
                </Text>
              )}

              <HStack>
                <Icon as={FaIndustry as ElementType} color="#006400" />
                <Text fontWeight="medium">Industry:</Text>
                <Text>{org.industry || "N/A"}</Text>
              </HStack>

              <HStack>
                <Icon as={FaEnvelope as ElementType} color="#006400" />
                <Text fontWeight="medium">Email:</Text>
                <Text>{org.email || "N/A"}</Text>
              </HStack>

              <HStack>
                <Icon as={FaPhone as ElementType} color="#006400" />
                <Text fontWeight="medium">Phone:</Text>
                <Text>{org.phone_number || "N/A"}</Text>
              </HStack>

              <HStack>
                <Icon as={FaGlobe as ElementType} color="#006400" />
                <Text fontWeight="medium">Website:</Text>
                <Text>{org.website || "N/A"}</Text>
              </HStack>

              <HStack>
                <Icon as={FaMapMarkerAlt as ElementType} color="#006400" />
                <Text fontWeight="medium">Address:</Text>
                <Text>{org.address || "N/A"}</Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Subscription Info */}
        <Card shadow="sm" borderRadius="2xl">
          <CardHeader borderBottomWidth="1px">
            <HStack>
              <Icon as={FaCrown as ElementType} color="#006400" />
              <Heading size="md">Subscription Info</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {sub ? (
              <VStack align="start" spacing={3}>
                <HStack>
                  <Text fontWeight="medium">Plan:</Text>
                  <Tag colorScheme="green">{sub.tier_display}</Tag>
                </HStack>

                <HStack>
                  <Icon as={FaClock as ElementType} color="#006400" />
                  <Text fontWeight="medium">Joined:</Text>
                  <Text>{new Date(sub.date_joined).toLocaleDateString()}</Text>
                </HStack>

                <HStack>
                  {sub.is_trial ? (
                    <Tag colorScheme="orange">Trial - {sub.trial_days} days</Tag>
                  ) : (
                    <Tag colorScheme="green">Paid</Tag>
                  )}
                </HStack>

                <HStack>
                  <Text fontWeight="medium">Billing Cycle:</Text>
                  <Text>{sub.billing_cycle_days} days</Text>
                </HStack>

                <HStack>
                  <Text fontWeight="medium">Next Billing:</Text>
                  <Text>
                    {sub.next_billing_date
                      ? new Date(sub.next_billing_date).toLocaleDateString()
                      : "N/A"}
                  </Text>
                </HStack>
              </VStack>
            ) : (
              <Text color="gray.500">No subscription details found.</Text>
            )}
          </CardBody>
          <Button
            leftIcon={<Icon as={FiEdit2 as ElementType} />}
            colorScheme="green"
            size={'sm'}
            m={10}
            bg={'#006400'}
            onClick={()=>navigate(`/organization/${org.id}/subscription/`)}
            data-testid="edit-org-btn"
          >
            Edit
          </Button>
        </Card>
      </Grid>
      <DeleteOrganizationComponent />
      {/* Metadata */}
      <Box mt={2}>
        <Divider mb={2} />
        <HStack spacing={6}>
          <HStack>
            <Icon as={FaClock as ElementType} color="#006400" />
            <Text fontSize="sm" color="gray.600">
              Created: {new Date(org.created_at).toLocaleString()}
            </Text>
          </HStack>
          <HStack>
            <Icon as={FaClock as ElementType} color="#006400" />
            <Text fontSize="sm" color="gray.600">
              Updated: {new Date(org.updated_at).toLocaleString()}
            </Text>
          </HStack>
        </HStack>
      </Box>
    </Box>
  );
};

export default OrganizationManagementPage;
